import { useEffect, useRef, useState } from "react";
import queryClient from "@/query-client";
import { useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useTabParam, type TabDef } from "@/hooks/useTabParam";
import { LinkTabs } from "@/components/ui/link-tabs";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RefreshCw, MoreVertical, Loader2 } from "lucide-react";
import { PageTitle } from "@/components/shared/PageTitle";
import { DatedVersionSelector } from "@/components/shared/DatedVersionSelector";
import { useAppData } from "@/contexts/AppDataContext";
import {
  brandIntelligenceKeys,
  useGetBrandIntelligenceOverview,
  useGetBrandIntelligenceBuild,
  useGetBrandIntelligenceCategoryChoice,
  useRefreshBrandIntelligence,
} from "@/resources/brand-intelligence/brand-intelligence.api";
import { CategoriesTab } from "@/components/brand-intelligence/CategoriesTab";
import { CompetitorsTab } from "@/components/brand-intelligence/CompetitorsTab";
import { KeywordSovTab } from "@/components/brand-intelligence/KeywordSovTab";
import { AiVisibilityTab } from "@/components/brand-intelligence/AiVisibilityTab";
import { BrandIntelligenceBuildingState } from "@/components/brand-intelligence/BrandIntelligenceBuildingState";
import { handleApiError } from "@/utils";
import { plural, fmtDate } from "@/components/brand-intelligence/headerFormat";
import { BrandIntelligenceHeaderMeta } from "@/components/brand-intelligence/BrandIntelligenceHeaderMeta";
import { AddBrandModal } from "@/components/shared/AddBrandModal";
import { BrandCategoryBadge } from "@/components/brand-intelligence/BrandCategoryBadge";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";

// AUT-245: Brand Intelligence page. Layout/styling mirrors the signal-v3 prototype;
// all data is wired to the real API (see AUT-245-ROADMAP.md).

const ALL_TABS: TabDef[] = [
  { id: "categories", label: "Categories" },
  { id: "competitors", label: "Competitors" },
  { id: "keyword-sov", label: "Keyword SOV" },
  { id: "ai-visibility", label: "AI Visibility" },
];

const DEFAULT_TAB = ALL_TABS[0].id;

const emptyState = (msg: string, error = false) => (
  <div
    className={`rounded-sm border border-dashed py-12 text-center text-sm ${error ? "text-status-error-strong" : "text-muted-foreground"}`}
  >
    {msg}
  </div>
);

export default function BrandIntelligence() {
  const [searchParams] = useSearchParams();
  const { selectedBrandId } = useAppData();
  const { data: overview, isLoading, isError, isPlaceholderData } = useGetBrandIntelligenceOverview(selectedBrandId);
  const { data: build } = useGetBrandIntelligenceBuild(selectedBrandId);
  const { data: choice } = useGetBrandIntelligenceCategoryChoice(selectedBrandId);
  const readOnly = useViewerReadOnly();
  const refresh = useRefreshBrandIntelligence();

  // Where focus returns when the edit dialog closes (the badge, or the "Select a primary category" button).
  const categoryTriggerRef = useRef<HTMLElement | null>(null);
  // The edit dialog belongs to the brand on screen: a brand switch closes it and switching back does not reopen it.
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);
  const [lastBrandId, setLastBrandId] = useState(selectedBrandId);
  if (lastBrandId !== selectedBrandId) {
    setLastBrandId(selectedBrandId);
    setEditingBrandId(null);
  }
  // FR-7: a save refetches only Keyword SOV, which reads this counter.
  const [choiceSaveCount, setChoiceSaveCount] = useState(0);

  const TABS = ALL_TABS;

  // AUT-256 made these real anchors. AUT-399 moved the mechanics into `useTabParam` / `LinkTabs`
  // so `Profile` could reuse them instead of carrying a second copy of the hover-specificity trap.
  const { activeTab, tabHref } = useTabParam(TABS, DEFAULT_TAB);

  const isBuilding = build?.status === "building" || build?.status === "pending";
  // Switching brands: the global keepPreviousData default keeps the previous brand's data on screen
  // while the new brand loads. Show a loader (and hide the stale header) so no cross-brand data is
  // misread during the switch. isPlaceholderData = we're currently showing another brand's cached data.
  const isSwitchingBrand = !!selectedBrandId && !isBuilding && (isLoading || isPlaceholderData);

  // AUT-324: when a rebuild finishes, refetch the brand's data so the page updates on its own.
  // The page-level overview query stays mounted through the build (the header + Categories tab
  // read from it), so without this it would keep the pre-refresh data until a manual reload.
  const wasBuilding = useRef(false);
  useEffect(() => {
    if (wasBuilding.current && build?.status === "complete") {
      queryClient.invalidateQueries({ queryKey: brandIntelligenceKeys.all });
    }
    wasBuilding.current = isBuilding;
  }, [build?.status, isBuilding]);

  const [cooldownOpen, setCooldownOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleRefresh = (acknowledge = false) => {
    if (!selectedBrandId) return;
    refresh.mutate(
      { brandId: selectedBrandId, acknowledge },
      {
        onSuccess: () => {
          toast.success("Brand Intelligence refresh started.");
          setCooldownOpen(false);
          setAcknowledged(false);
        },
        onError: (err) => {
          const status = err instanceof AxiosError ? err.response?.status : undefined;
          // Section 1.2: cooldown bypass requires an explicit acknowledgment checkbox.
          if (status === 429 && !acknowledge) {
            setCooldownOpen(true);
            return;
          }
          handleApiError(err);
        },
      },
    );
  };

  const header = overview?.header;
  // FR-3: no badge on another brand's placeholder overview, without a report, or during a first build.
  const firstBuild = isBuilding && !build?.refreshed_at;
  const showSlot = !!choice && choice.has_report && !isPlaceholderData && !firstBuild;
  const hasChoice = showSlot && choice.categories.length > 0;
  const refreshedAt = fmtDate(build?.refreshed_at);

  const renderTab = (id: string, guard: string, node: React.ReactNode) => {
    if (activeTab !== id) return null;
    if (!selectedBrandId) return emptyState(guard);
    return node;
  };

  return (
    <div className="-m-9 p-9 bg-card space-y-6 min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <PageTitle title="Brand Intelligence" brandScoped />
          {header && !isSwitchingBrand ? (
            <BrandIntelligenceHeaderMeta
              header={header}
              refreshedAt={refreshedAt}
              badge={showSlot ? <BrandCategoryBadge categories={choice.categories} readOnly={readOnly} onEdit={() => setEditingBrandId(selectedBrandId)} focusRef={(el) => { categoryTriggerRef.current = el; }} /> : null}
              categoryCountScope={hasChoice ? "other" : "all"}
              categoryCount={hasChoice ? plural(header.category_count - choice.categories.length, "other category", "other categories") : plural(header.category_count, "category", "categories")}
            />
          ) : (
            <div className="mt-1 text-sm text-muted-foreground">
              Category, competitor, and keyword share-of-voice intelligence across the brand's full Amazon catalog.
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {refreshedAt && (
            <DatedVersionSelector
              versions={[{ id: build!.refreshed_at as string, label: refreshedAt }]}
              value={build!.refreshed_at as string}
              onValueChange={() => {}}
            />
          )}
          <Button
            variant="default"
            className="gap-1.5"
            onClick={() => handleRefresh(false)}
            disabled={!selectedBrandId || isBuilding || refresh.isPending}
          >
            <RefreshCw className={`h-4 w-4 ${isBuilding ? "animate-spin" : ""}`} />
            {isBuilding ? "Building…" : "Refresh"}
          </Button>
          <Button variant="ghost" size="icon" aria-label="More options">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {selectedBrandId && overview && choice && (
        <AddBrandModal
          open={editingBrandId === selectedBrandId}
          onOpenChange={(o) => { if (!o) setEditingBrandId(null); }}
          brandId={selectedBrandId}
          brandName={overview.brand}
          reportCategories={overview.categories}
          onSaved={() => setChoiceSaveCount((c) => c + 1)}
          returnFocusRef={categoryTriggerRef}
        />
      )}

      <LinkTabs tabs={TABS} activeTab={activeTab} tabHref={tabHref} />

      {isBuilding && build ? (
        // Spec 1.1: every tab shows the identical building card while a build runs.
        <BrandIntelligenceBuildingState build={build} />
      ) : isSwitchingBrand ? (
        // Brand switch in flight: show a loader instead of the previous brand's (stale) data.
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading Brand Intelligence…
        </div>
      ) : (
        <>
          {renderTab(
            "categories",
            "Select a brand to see its Brand Intelligence.",
            isLoading
              ? emptyState("Loading categories…")
              : isError || !overview
                ? emptyState("Could not load Brand Intelligence for this brand.", true)
                : <CategoriesTab brandId={selectedBrandId!} categories={overview.categories} />,
          )}
          {renderTab("competitors", "Select a brand to see its competitor landscape.", <CompetitorsTab brandId={selectedBrandId!} />)}
          {renderTab("keyword-sov", "Select a brand to see its Keyword SOV.", <KeywordSovTab brandId={selectedBrandId!} choiceSaveCount={choiceSaveCount} />)}
          {renderTab("ai-visibility", "Select a brand to see its AI visibility.", <AiVisibilityTab brandId={selectedBrandId!} />)}
        </>
      )}

      {/* Section 1.2: cooldown-bypass acknowledgment. */}
      <Dialog open={cooldownOpen} onOpenChange={(o) => { setCooldownOpen(o); if (!o) setAcknowledged(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Refresh again so soon?</DialogTitle>
            <DialogDescription>
              This brand was refreshed within the cooldown window. Refreshing again re-runs the Snowflake queries.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-2 py-2 text-sm">
            <Checkbox checked={acknowledged} onCheckedChange={(v) => setAcknowledged(v === true)} className="mt-0.5" />
            <span>I understand this may trigger additional Snowflake usage.</span>
          </label>
          <DialogFooter>
            <Button variant="outline-on-white" onClick={() => setCooldownOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={() => handleRefresh(true)} disabled={!acknowledged || refresh.isPending} className="rounded-lg">Refresh anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
